import React, { useEffect, useState } from "react";
import { PropTypes } from "prop-types";
import { withTranslation, useTranslation } from "react-i18next";
import { useDropArea } from "react-use";
import { mdiPlusCircleOutline, mdiInformation } from "@mdi/js";
import Icon from "@mdi/react";

import Button, { BUTTON_VARIANTS } from "../../button/Button";
import Modal from "../../modal/Modal";

import LoaderEditor from "components/utils/loaderEditor/LoaderEditor";
import NotifMessage from "components/common/tools/NotifMessage";
import UsersAdminCardModal from "./UsersAdminCardModal";
import Tooltip from "../../tooltip/Tooltip";

import "css/admin/users.scss";
import "css/admin/utils/upload-button.scss";

function ModalHeader({ importedUsers }) {
  const { t } = useTranslation("editor-users");

  return (
    <div className="flex flex-wrap items-center gap-3">
      <Icon path={mdiPlusCircleOutline} size={1.25} />
      <div className="shrink-0 grow basis-0 text-[1.171875rem] font-semibold">
        {t("usersImport")}
        {!!importedUsers.length && <span> ({importedUsers.length})</span>}
      </div>
    </div>
  );
}

function UsersAdminModal({
  cleanImport,
  importError,
  importedUsers,
  open,
  onClose,
  upload,
  t,
}) {
  const [bond] = useDropArea({
    onFiles: (files) => upload(files[0]),
  });

  const [uploading, setUploading] = useState(false);
  const [finished, setFinished] = useState(false);
  const [file, setFile] = useState("");
  const [filename, setFilename] = useState("");
  const [type, setType] = useState(null);
  const [notifications, setNotifications] = useState({
    create_password: false,
    registration: false,
  });
  const [error, setError] = useState("");

  useEffect(() => {
    if (importedUsers.length > 0) {
      setFinished(true);
      setUploading(false);
    }
  }, [importedUsers]);

  useEffect(() => {
    if (importError) setUploading(false);
  }, [importError]);

  const onExit = (e, reason) => {
    if (
      uploading &&
      (reason === "escapeKeyDown" || reason === "backdropClick")
    ) {
      return;
    }
    onClose();

    cleanImport();
    setError("");
    setUploading(false);
    setFinished(false);
    setFile("");
    setFilename("");
    setType(null);
    setNotifications({
      create_password: false,
      registration: false,
    });
  };

  const onSelectFile = (e) => {
    e.preventDefault();
    const file = e.target.files[0];

    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result;
        let type = null;
        if (base64) setFile(base64);

        if (file.type === "text/csv") type = "csv";
        if (file.type === "application/json") type = "json";
        if (!type) {
          setError(t("editor-errors:errorType"));
        } else {
          setError("");
          setType(type);
          setFilename(file.name);
        }
      };
    }
  };

  const onUpload = () => {
    upload(file, type, notifications);
    setUploading(true);
  };

  let validationComponent = null;

  if (error || importError) {
    validationComponent = (
      <NotifMessage message={t(error || importError)} severity="error" />
    );
  }

  const modalActions = finished
    ? [
        {
          label: t("modal:close"),
          buttonVariant: BUTTON_VARIANTS.PRIMARY,
          isFullWidth: true,
          isCloseAfterAction: true,
          onClick: onExit,
        },
      ]
    : [
        {
          label: t("modal:cancel"),
          buttonVariant: BUTTON_VARIANTS.SECONDARY,
          isFullWidth: true,
          isCloseAfterAction: true,
          onClick: onExit,
        },
        {
          label: t("modal:import"),
          buttonVariant: BUTTON_VARIANTS.PRIMARY,
          isFullWidth: true,
          isCloseAfterAction: false,
          isDisabled: (!file && !type) || error || uploading,
          onClick: onUpload,
        },
      ];

  return (
    <Modal
      actions={modalActions}
      header={<ModalHeader importedUsers={importedUsers} />}
      id="new-platform-modal"
      isOpen={open}
      onClose={onExit}
      size="md"
      className="min-h-100"
    >
      <div className="users-admin-modal-body">
        {!uploading && (!finished || importError) && (
          <>
            {validationComponent}
            <div className="editor-form-section">
              <div
                className="editor-form-input"
                style={{ maxWidth: "400px", minWidth: "320px" }}
              >
                <div className="editor-form-inline">
                  <div className="upload-button-container" {...bond}>
                    <label
                      htmlFor="upload-button-input"
                      className="upload-button"
                    >
                      <a className="upload-button-link">
                        <div className="upload-button-label">
                          <i className="material-icons">add</i>
                          <span>{t("fileCsvJson")}</span>
                        </div>
                      </a>
                      <input
                        id="upload-button-input"
                        className="upload-label-input upload-button-input"
                        type="file"
                        disabled={!upload}
                        onChange={(e) => (upload ? onSelectFile(e) : null)}
                      />
                    </label>
                  </div>
                </div>
                {filename && (
                  <p>
                    {" "}
                    {t("filename")}: {filename}{" "}
                  </p>
                )}
              </div>
            </div>
            <div className="editor-form-section flex flex-col gap-3">
              <div className="text-md flex gap-3 font-semibold">
                {t("notifications")}
                <span
                  data-tooltip-content={t("infoNotifications")}
                  data-tooltip-id="user-modal-tooltip"
                >
                  <Icon path={mdiInformation} size={0.8} />
                </span>
              </div>
              <div
                className="editor-form-input"
                style={{ maxWidth: "400px", minWidth: "320px" }}
              >
                <div className="editor-form-inline editor-form-notifications">
                  <label className="checkbox">
                    <span>{t("registration")}</span>
                    <input
                      onChange={(e) => {
                        setNotifications({
                          ...notifications,
                          registration: e.target.checked,
                        });
                      }}
                      checked={notifications.registration}
                      id="notif_registration"
                      name="notif_registration"
                      type="checkbox"
                      className="checkbox-input"
                    />
                    <span className="checkmark"> </span>
                  </label>
                </div>
                <div className="editor-form-inline editor-form-notifications">
                  <label className="checkbox">
                    <span>{t("create_password")}</span>
                    <input
                      onChange={(e) => {
                        setNotifications({
                          ...notifications,
                          create_password: e.target.checked,
                        });
                      }}
                      checked={notifications.create_password}
                      id="notif_create_password"
                      name="notif_create_password"
                      type="checkbox"
                      className="checkbox-input"
                    />
                    <span className="checkmark"> </span>
                  </label>
                </div>
              </div>
            </div>
          </>
        )}
        {!uploading && finished && (
          <UsersAdminCardModal users={importedUsers} errors={importError} />
        )}
        {uploading && (
          <div>
            <p>{t("uploading")}</p>
            <LoaderEditor loading={uploading} />
          </div>
        )}
      </div>
      <Tooltip id="user-modal-tooltip" />
    </Modal>
  );
}

UsersAdminModal.propTypes = {
  cleanImport: PropTypes.func,
  importError: PropTypes.any,
  importedUsers: PropTypes.array,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  upload: PropTypes.func,
  t: PropTypes.func.isRequired,
};

export default withTranslation([
  "editor-users",
  "editor-errors",
  "languages",
  "modal",
])(UsersAdminModal);
